import { appData } from "../state/state.js";
import {
  getNotificationEventDefinition,
} from "./notificationRegistryAdapter.js";
import {
    escapeHTML,
    sanitizeHTML,
} from "../../shared/utils/contentUtils.js";
import {
    findItem,
} from "../../shared/utils/itemLookup.js";
import {
    formatDate,
} from "../../shared/utils/dateUtils.js";

function getReadableValue(id, type) {
  if (!id || !type) {return id;}

  const config = appData.config;
  let sourceArray;

  switch (type) {
    case "patientStatus":
      sourceArray = config.patientStatus;
      break;
    case "projectStatus":
      sourceArray = config.projectStatus;
      break;
    case "taskStatus":
      sourceArray = config.taskStatus;
      break;
    case "issueStatus":
      sourceArray = config.issueStatus;
      break;
    case "maintenanceStatus":
      sourceArray = config.maintenanceStatus;
      break;
    case "labStatus":
      sourceArray = config.labStatus;
      break;
    case "priorities":
      sourceArray = config.priorities;
      break;
    case "urgencyLevels":
      sourceArray = config.urgencyLevels;
      break;
    default:
      sourceArray = null;
  }

  if (!sourceArray) {
    console.warn(`getReadableValue: No config array found for type "${type}"`);
    return id;
  }

  const item = sourceArray.find((entry) => entry.id === id);
  return item?.name || id;
}

function stripHtml(value) {
  return value ? String(value).replace(/<[^>]*>?/gm, "") : "";
}

function formatShortWeekdayDate(dateValue) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dateValue || ""));
  if (!match) {return String(dateValue || "");}

  const [, year, month, day] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  const weekday = new Intl.DateTimeFormat("en-AU", {
    weekday: "short",
  }).format(date);
  return `${weekday}, ${day}/${month}/${year}`;
}

function formatEmbeddedDateKeys(value) {
  return String(value || "").replace(
    /\b(\d{4}-\d{2}-\d{2})\b/g,
    (dateKey) => formatShortWeekdayDate(dateKey),
  );
}

function getLeaveContextDateText(contextName) {
  const contextText = formatEmbeddedDateKeys(contextName);
  const separatorIndex = contextText.indexOf(":");
  return separatorIndex >= 0 ?
    contextText.slice(separatorIndex + 1).trim() :
    contextText;
}

function getLeaveOutcomeLabel(notification) {
  const requestedAction = notification?.detailSnapshot?.requestedAction;
  if (requestedAction === "edit") {return "leave edit";}
  if (requestedAction === "cancel") {return "leave cancellation";}
  return "leave request";
}

function getMaintenanceStreamLabel(notification) {
  return notification?.detailSnapshot?.streamLabel ||
    (notification?.contextType === "issue" ? "Issue" : "") ||
    (notification?.contextType === "service" ? "Service" : "") ||
    (String(notification?.triggerEvent || "").includes("_issue_") ?
      "Issue" :
      "") ||
    "Maintenance";
}

function isGeneratedServiceTitleForAsset(title, assetName) {
  const name = String(assetName || "").trim();
  const value = String(title || "").trim();
  if (!name || !value) {return false;}
  return new RegExp(
    `^[A-Z][a-z]{2} \\d{4} Service: ${name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}$`,
  ).test(value);
}

function getMaintenanceLinkedAssetName(notification) {
  const snapshot = notification?.detailSnapshot || {};
  if (snapshot.linkedAssetName) {return snapshot.linkedAssetName;}

  const maintenance = notification?.parentId ?
    findItem(notification.parentId) :
    null;
  if (maintenance?.linked_asset_name) {return maintenance.linked_asset_name;}

  const linkedAssetId = snapshot.linkedAssetId || maintenance?.linked_asset_id;
  const linkedAsset = linkedAssetId ? findItem(linkedAssetId) : null;
  return linkedAsset?.name || "";
}

function shouldShowMaintenanceAssetContext(notification, context) {
  const assetName = getMaintenanceLinkedAssetName(notification);
  if (!assetName) {return false;}

  const stream = getMaintenanceStreamLabel(notification).toLowerCase();
  const snapshot = notification?.detailSnapshot || {};
  return !(stream === "service" && (
    snapshot.isGeneratedServiceTitle === true ||
    isGeneratedServiceTitleForAsset(context.parentName, assetName)
  ));
}

function getMaintenanceAssetSuffix(notification, context) {
  if (!shouldShowMaintenanceAssetContext(notification, context)) {return "";}
  return ` on <strong>${escapeHTML(getMaintenanceLinkedAssetName(notification))}</strong>`;
}

function getLabTypeText(notification, context) {
  const typeLabels = notification?.detailSnapshot?.typeLabels || [];
  if (typeLabels.length > 0) {
    return escapeHTML(typeLabels.join(", "));
  }
  if (notification?.contextType === "lab" && context.contextName) {
    return context.contextName;
  }
  return "a lab item";
}

function getLabContextText(context) {
  return context.parentName || "this Lab item";
}

function renderLabEventLead(notification, context, actionText, trailingText = "") {
  return `${actionText} ${getLabTypeText(notification, context)} for ` +
    `<strong>${getLabContextText(context)}</strong>${trailingText}.`;
}

function getTriggerName(triggeredBy) {
  const triggerUser = appData.users.find((user) => user.id === triggeredBy);
  const name = triggerUser?.name ? triggerUser.name.split(" ")[0] : "System";
  return escapeHTML(name);
}

function getCalendarEventDateText(notification) {
  const snapshot = notification?.detailSnapshot || {};
  const startDate = snapshot.startDate || notification?.dueDate || "";
  const endDate = snapshot.endDate || startDate;
  const startLabel = startDate ? formatDate(startDate) : "the calendar";
  const endLabel = endDate && endDate !== startDate ? formatDate(endDate) : "";
  const dateLabel = endLabel ? `${startLabel} - ${endLabel}` : startLabel;

  if (snapshot.isFullDay === true) {return dateLabel;}
  if (snapshot.startTime && snapshot.endTime) {
    return `${dateLabel}, ${snapshot.startTime} - ${snapshot.endTime}`;
  }
  if (snapshot.startTime) {return `${dateLabel}, ${snapshot.startTime}`;}
  return dateLabel;
}

function getCalendarReminderDateText(notification) {
  const snapshot = notification?.detailSnapshot || {};
  const date = snapshot.reminderDate || notification?.dueDate || "";
  return date ? formatDate(date) : "the calendar";
}

function getCalendarLeaveDateText(notification) {
  const snapshot = notification?.detailSnapshot || {};
  const startDate = snapshot.startDate || notification?.dueDate || "";
  const endDate = snapshot.endDate || startDate;
  const startLabel = startDate ? formatDate(startDate) : "leave";
  const endLabel = endDate && endDate !== startDate ? formatDate(endDate) : "";
  return endLabel ? `${startLabel} - ${endLabel}` : startLabel;
}

function getCalendarThresholdLeadText(notification, options = {}) {
  const metadata = notification?.calendarThresholdMetadata || {};
  if (metadata.notificationKind !== "threshold") {return "";}

  const value = Number(metadata.thresholdValue);
  const unit = String(metadata.thresholdUnit || "");
  if (!Number.isInteger(value) || value < 1) {return "";}
  if (!["minutes", "hours", "days"].includes(unit)) {return "";}

  const minuteLabel = options.shortMinutes ?
    (value === 1 ? "min" : "mins") :
    (value === 1 ? "minute" : "minutes");
  const unitLabels = {
    minutes: minuteLabel,
    hours: value === 1 ? "hour" : "hours",
    days: value === 1 ? "day" : "days",
  };
  return `${value} ${unitLabels[unit]}`;
}

function resolveNotificationNames(notification) {
  let parentName = notification.parentName;
  let contextName = notification.contextName;

  if (!parentName && notification.parentId) {
    const parentItem = findItem(notification.parentId);
    parentName = parentItem?.name;
  }
  if (!contextName && notification.contextId) {
    const contextItem = findItem(notification.contextId);
    contextName = contextItem?.name;
  }

  const escapedParentName = parentName ? escapeHTML(parentName) : "";
  const escapedContextName = contextName ? escapeHTML(contextName) : "";
  const parentTypeLabel = notification.parentType
    ? `a ${notification.parentType.slice(0, -1)}`
    : "an item";
  const contextTypeLabel = notification.contextType
    ? notification.contextType.replace("_", " ")
    : "an item";

  return {
    parentName: escapedParentName || parentTypeLabel,
    contextName: escapedContextName ||
      (notification.contextType ? contextTypeLabel : null),
    rawContextName: contextName || "",
    parentSuffix: (escapedParentName && notification.contextType)
      ? ` on ${escapedParentName}`
      : "",
  };
}

function getReadableNewValue(notification) {
  const event = notification.triggerEvent;
  const newValueRaw = notification.newValue;
  if (!newValueRaw) {return "";}

  let lookupType = "";
  if (event === "status_change") {
    if (notification.parentType === "patients") {lookupType = "patientStatus";}
    else if (notification.parentType === "projects") {
      lookupType = "projectStatus";
    } else if (notification.parentType === "maintenance") {
      lookupType = "maintenanceStatus";
    } else if (notification.parentType === "labs") {
      lookupType = "labStatus";
    } else if (notification.contextType === "issue_log") {
      lookupType = "issueStatus";
    }
  } else if (event === "task_status_change") {
    lookupType = "taskStatus";
  } else if (event === "urgency_change") {
    lookupType = "priorities";
  }

  const value = lookupType
    ? getReadableValue(newValueRaw, lookupType)
    : String(newValueRaw);
  return ` to <strong>${escapeHTML(value)}</strong>`;
}

const NOTIFICATION_RENDERERS = Object.freeze({
  tag_note: ({ triggerName, context }) =>
    `${triggerName} tagged you in a note on <strong>${context.parentName}</strong>.`,
  tag_comment: ({ triggerName, context }) =>
    `${triggerName} tagged you in a comment on <strong>${context.parentName}</strong>.`,
  task_assign: ({ triggerName, context }) =>
    `${triggerName} assigned you to <strong>${context.contextName}</strong>${context.parentSuffix}.`,
  task_owner: ({ triggerName, context }) =>
    `${triggerName} assigned you to <strong>${context.contextName}</strong>${context.parentSuffix}.`,
  parent_manager: ({ triggerName, context }) =>
    `${triggerName} made you a manager of <strong>${context.parentName}</strong>.`,
  patient_provider_added: ({ triggerName, context }) =>
    `${triggerName} added you as a provider on <strong>${context.parentName}</strong>.`,
  labs_provider_added: ({ notification, triggerName, context }) =>
    renderLabEventLead(notification, context, `${triggerName} added you as a provider on`),
  labs_assigned_user_added: ({ notification, triggerName, context }) =>
    renderLabEventLead(notification, context, `${triggerName} assigned you to`),
  labs_patient_case_manager_new_lab: ({ notification, triggerName, context }) =>
    `${triggerName} created ${getLabTypeText(notification, context)} for ` +
      `patient <strong>${context.contextName || getLabContextText(context)}</strong>.`,
  user_invite_claimed: ({ context }) => {
    const label = context.contextName || context.parentName || "an invited user";
    return `<strong>${label}</strong> signed in and claimed their invite.`;
  },
  event_created: ({ triggerName, context, notification }) =>
    `${triggerName} created event ` +
      `<strong>${context.contextName}</strong> on ` +
      `${escapeHTML(getCalendarEventDateText(notification))}.`,
  event_updated: ({ triggerName, context, notification }) =>
    `${triggerName} updated event ` +
      `<strong>${context.contextName}</strong> on ` +
      `${escapeHTML(getCalendarEventDateText(notification))}.`,
  event_deleted: ({ triggerName, context, notification }) =>
    `${triggerName} deleted event ` +
      `<strong>${context.contextName}</strong> on ` +
      `${escapeHTML(getCalendarEventDateText(notification))}.`,
  leave_approval_required: ({ context }) =>
    `<strong>Approval needed:</strong> Leave request ` +
      `${formatEmbeddedDateKeys(context.contextName)}`,
  leave_approved: ({ notification, context }) =>
    `<strong>Approved:</strong> Your ${getLeaveOutcomeLabel(notification)} for ` +
      `${getLeaveContextDateText(context.contextName)} was approved.`,
  leave_rejected: ({ notification, context }) => {
    const reasonText = stripHtml(notification.newValue) || "None";
    return `<strong>Rejected:</strong> Your ${getLeaveOutcomeLabel(notification)} for ` +
      `${getLeaveContextDateText(context.contextName)} was rejected.<br>` +
      `<small style="font-style: italic; font-size: 0.85em;">` +
      `Reason: ${escapeHTML(reasonText)}</small>`;
  },
  leave_created_for_you: ({ triggerName, context }) =>
    `${triggerName} created leave for you for ` +
      `${getLeaveContextDateText(context.contextName)}.`,
  leave_updated_for_you: ({ triggerName, context }) =>
    `${triggerName} updated your leave for ` +
      `${getLeaveContextDateText(context.contextName)}.`,
  leave_cancelled_for_you: ({ triggerName, context }) =>
    `${triggerName} cancelled your leave for ` +
      `${getLeaveContextDateText(context.contextName)}.`,
  maintenance_manager: ({ notification, triggerName, context }) =>
    `${triggerName} made you the manager of <strong>${context.parentName}</strong>` +
      `${getMaintenanceAssetSuffix(notification, context)}.`,
  new_note: ({ notification, triggerName, context }) =>
    notification.parentType === "labs" ?
      renderLabEventLead(notification, context, `${triggerName} added a new note to`) :
      `${triggerName} added a new note to <strong>${context.parentName}</strong>` +
        `${notification.parentType === "maintenance" ?
          getMaintenanceAssetSuffix(notification, context) :
          ""}.`,
  new_upload: ({ notification, triggerName, context }) => {
    if (notification.parentType === "labs") {
      return renderLabEventLead(
          notification,
          context,
          `${triggerName} uploaded a file to`,
      );
    }
    const item = notification.contextType === "task" ?
      context.contextName :
      context.parentName;
    const suffix = notification.contextType === "task" ?
      context.parentSuffix :
      "";
    const maintenanceSuffix = notification.parentType === "maintenance" ?
      getMaintenanceAssetSuffix(notification, context) :
      "";
    return `${triggerName} uploaded a file to <strong>${item}</strong>` +
      `${maintenanceSuffix || suffix}.`;
  },
  item_closed: ({ notification, triggerName, context }) =>
    notification.parentType === "labs" ?
      renderLabEventLead(notification, context, `${triggerName} closed`) :
      `${triggerName} closed <strong>${context.parentName}</strong>` +
        `${notification.parentType === "maintenance" ?
          getMaintenanceAssetSuffix(notification, context) :
          ""}.`,
  task_closed: ({ triggerName, context }) =>
    `${triggerName} closed <strong>${context.contextName}</strong>${context.parentSuffix}.`,
  status_change: ({ notification, triggerName, context, newValue }) => {
    if (notification.parentType === "labs") {
      return renderLabEventLead(
          notification,
          context,
          `${triggerName} changed the status of`,
          newValue,
      );
    }
    const isContextItem = notification.contextType === "task" ||
      notification.contextType === "issue_log";
    const item = isContextItem ? context.contextName : context.parentName;
    const suffix = notification.parentType === "maintenance" ?
      getMaintenanceAssetSuffix(notification, context) :
      (isContextItem ? context.parentSuffix : "");
    return `${triggerName} changed the status of <strong>${item}</strong>` +
      `${suffix}${newValue}.`;
  },
  task_status_change: ({ triggerName, context, newValue }) =>
    `${triggerName} changed the status of <strong>${context.contextName}</strong>${newValue}${context.parentSuffix}.`,
  urgency_change: ({ notification, triggerName, context, newValue }) => {
    if (notification.parentType === "labs") {
      return renderLabEventLead(
          notification,
          context,
          `${triggerName} changed the urgency of`,
          newValue,
      );
    }
    const isContextItem = notification.contextType === "task" ||
      notification.contextType === "issue_log";
    const item = isContextItem ? context.contextName : context.parentName;
    const suffix = notification.parentType === "maintenance" ?
      getMaintenanceAssetSuffix(notification, context) :
      (isContextItem ? context.parentSuffix : "");
    return `${triggerName} changed the urgency of <strong>${item}</strong>` +
      `${suffix}${newValue}.`;
  },
  date_change: ({ notification, triggerName, context }) => {
    if (notification.parentType === "labs") {
      const formattedDate = formatDate(notification.newValue, "date");
      return renderLabEventLead(
          notification,
          context,
          `${triggerName} changed the due date of`,
          ` to <strong>${escapeHTML(formattedDate)}</strong>`,
      );
    }
    const item = notification.contextType === "task" ?
      context.contextName :
      context.parentName;
    const suffix = notification.parentType === "maintenance" ?
      getMaintenanceAssetSuffix(notification, context) :
      (notification.contextType === "task" ? context.parentSuffix : "");
    const formattedDate = formatDate(notification.newValue, "date");
    return `${triggerName} changed the due date of <strong>${item}</strong>` +
      `${suffix} to <strong>${escapeHTML(formattedDate)}</strong>.`;
  },
  task_due_today: ({ context }) =>
    `Due Today: <strong>${context.contextName}</strong> is due today${context.parentSuffix}.`,
  project_due_today: ({ context }) =>
    `Due Today: <strong>${context.parentName}</strong> is due today.`,
  task_due_reminder: ({ notification, context }) => {
    const date = formatDate(notification.dueDate, "date");
    return `Due Soon: <strong>${context.contextName}</strong> is due on ` +
      `${escapeHTML(date)}${context.parentSuffix}.`;
  },
  project_due_reminder: ({ notification, context }) => {
    const date = formatDate(notification.dueDate, "date");
    return `Due Soon: <strong>${context.parentName}</strong> is due on ` +
      `${escapeHTML(date)}.`;
  },
  patient_appt_reminder: ({ notification, context }) => {
    const date = formatDate(notification.dueDate, "date");
    let time = "";
    if (notification.dueDate) {
      const rawDate = notification.dueDate.toDate ?
        notification.dueDate.toDate() :
        new Date(notification.dueDate);
      time = rawDate.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    }
    return `Reminder: <strong>${context.parentName}</strong> has an ` +
      `appointment on ${escapeHTML(date)} at ${escapeHTML(time)}.`;
  },
  task_overdue: ({ context }) =>
    `<strong>${context.contextName}</strong> is now overdue${context.parentSuffix}.`,
  project_overdue: ({ context }) =>
    `<strong>${context.parentName}</strong> is now overdue.`,
  task_inactive: ({ context }) =>
    `Inactive: <strong>${context.contextName}</strong> has had no recent activity.`,
  asset_task_inactive: ({ context }) =>
    `Inactive: <strong>${context.contextName}</strong> has had no recent activity${context.parentSuffix}.`,
  patient_inactive: ({ context }) =>
    `Inactive: <strong>${context.parentName}</strong> has had no recent activity.`,
  project_inactive: ({ context }) =>
    `Inactive: <strong>${context.parentName}</strong> has had no recent activity.`,
  maintenance_due_reminder: ({ notification, context }) => {
    const date = formatDate(notification.dueDate, "date");
    const stream = getMaintenanceStreamLabel(notification);
    return `Due Soon: <strong>${context.parentName}</strong> ` +
      `(${escapeHTML(stream)})${getMaintenanceAssetSuffix(notification, context)} ` +
      `is due on ${escapeHTML(date)}.`;
  },
  maintenance_overdue: ({ notification, context }) => {
    const stream = getMaintenanceStreamLabel(notification);
    return `<strong>${context.parentName}</strong> ` +
      `(${escapeHTML(stream)})${getMaintenanceAssetSuffix(notification, context)} ` +
      `is now overdue.`;
  },
  maintenance_inactive: ({ notification, context }) => {
    const stream = getMaintenanceStreamLabel(notification);
    return `${escapeHTML(stream)} Inactive: ` +
      `<strong>${context.parentName}</strong>` +
      `${getMaintenanceAssetSuffix(notification, context)} has had no recent activity.`;
  },
  labs_due_reminder: ({ notification, context }) => {
    const date = formatDate(notification.dueDate, "date");
    return `Reminder: ${getLabTypeText(notification, context)} for ` +
      `<strong>${getLabContextText(context)}</strong> is due on ` +
      `${escapeHTML(date)}.`;
  },
  labs_overdue: ({ notification, context }) =>
    `Overdue: ${getLabTypeText(notification, context)} for ` +
      `<strong>${getLabContextText(context)}</strong> is overdue.`,
  labs_inactive: ({ notification, context }) =>
    `Inactive: ${getLabTypeText(notification, context)} for ` +
      `<strong>${getLabContextText(context)}</strong> has had no recent activity.`,
  recipient_away: ({ notification, context }) => {
    const awayUserName = context.rawContextName ?
      context.rawContextName.split(" ")[0] :
      "User";
    const awayUntil = formatShortWeekdayDate(notification.newValue) ||
      notification.newValue;
    return `Reminder: <strong>${escapeHTML(awayUserName)}</strong> ` +
      `is away until ${escapeHTML(awayUntil)}.`;
  },
  ordering_item_status: ({ notification, triggerName, context }) => {
    const statusLabels = {
      ordering_item_ordered: "ordered",
      ordering_item_delivered: "delivered",
      ordering_item_cancelled: "cancelled",
      ordering_item_reopened: "reopened",
    };
    const status = statusLabels[notification.triggerEvent] || "updated";
    return `${triggerName} marked <strong>${context.parentName}</strong> ` +
      `as ${status}.`;
  },
  ordering_item_added: ({ triggerName, context }) => {
    const itemName = context.contextName || context.parentName;
    const bucketSuffix = context.contextName && context.parentName ?
      ` to <strong>${context.parentName}</strong>` : "";
    return `${triggerName} added <strong>${itemName}</strong>` +
      `${bucketSuffix} in Ordering.`;
  },
  team_patient_high_priority: ({ notification, context }) => {
    const urgencyName = getReadableValue(notification.newValue, "priorities") ||
      notification.newValue;
    return `Patient Alert: <strong>${context.parentName}</strong> is now ` +
      `marked as <strong>${escapeHTML(String(urgencyName))}</strong>.`;
  },
  team_patient_inactive: ({ context }) =>
    `Inactive Patient: <strong>${context.parentName}</strong> has had no recent activity.`,
  team_task_stalled: ({ context }) =>
    `Inactive High Urgency Task: <strong>${context.contextName}</strong>` +
      `${context.parentSuffix} has had no recent activity.`,
  team_asset_service: ({ notification, context }) => {
    const date = formatDate(notification.dueDate, "date");
    return `Service Due: <strong>${context.parentName}</strong> is due for ` +
      `service on ${escapeHTML(date)}.`;
  },
  team_asset_service_overdue: ({ notification, context }) => {
    const date = formatDate(notification.dueDate, "date");
    return `Service Overdue: <strong>${context.parentName}</strong> service ` +
      `was due on ${escapeHTML(date)}.`;
  },
  compliance_due: ({ notification }) => {
    const snapshot = notification.contentSnapshot || {};
    const stateLabels = {
      due_soon: "Due soon",
      due_now: "Due now",
      overdue: "Overdue",
    };
    const stateLabel = stateLabels[snapshot.dueState] || "Compliance due";
    const date = formatDate(snapshot.dueDate || notification.dueDate, "date");
    return `<strong>${stateLabel}:</strong> ` +
      `<strong>${escapeHTML(snapshot.requirementName || "Requirement")}</strong> ` +
      `for ${escapeHTML(snapshot.itemName || "tracked item")} in ` +
      `${escapeHTML(snapshot.categoryName || "Compliance")} is due ` +
      `${escapeHTML(date)}.`;
  },
  team_maintenance_due: ({ notification, context }) => {
    const date = formatDate(notification.dueDate, "date");
    const stream = getMaintenanceStreamLabel(notification);
    return `Maintenance Due: <strong>${context.parentName}</strong> ` +
      `(${escapeHTML(stream)})${getMaintenanceAssetSuffix(notification, context)} ` +
      `is due on ${escapeHTML(date)}.`;
  },
  team_maintenance_high_priority: ({ notification, context }) => {
    const urgencyName = getReadableValue(notification.newValue, "priorities") ||
      notification.newValue;
    const stream = getMaintenanceStreamLabel(notification);
    return `Maintenance Alert: <strong>${context.parentName}</strong> ` +
      `(${escapeHTML(stream)})${getMaintenanceAssetSuffix(notification, context)} ` +
      `is now marked as ` +
      `<strong>${escapeHTML(String(urgencyName))}</strong>.`;
  },
  team_maintenance_inactive: ({ notification, context }) => {
    const stream = getMaintenanceStreamLabel(notification);
    return `${escapeHTML(stream)} Inactive: ` +
      `<strong>${context.parentName}</strong>` +
      `${getMaintenanceAssetSuffix(notification, context)} has had no recent activity.`;
  },
  team_maintenance_issue_new: ({ notification, triggerName, context }) =>
    `New Maintenance Issue: ${triggerName} logged ` +
      `<strong>${context.parentName}</strong>` +
      `${getMaintenanceAssetSuffix(notification, context)}.`,
  team_maintenance_issue_closed: ({ notification, triggerName, context }) =>
    `Maintenance Issue Closed: ${triggerName} closed ` +
      `<strong>${context.parentName}</strong>` +
      `${getMaintenanceAssetSuffix(notification, context)}.`,
  team_labs_high_priority: ({ notification, context }) => {
    const urgencyName = getReadableValue(notification.newValue, "priorities") ||
      notification.newValue;
    return `Lab Alert: ${getLabTypeText(notification, context)} for ` +
      `<strong>${getLabContextText(context)}</strong> is now marked as ` +
      `<strong>${escapeHTML(String(urgencyName))}</strong>.`;
  },
  team_labs_inactive: ({ notification, context }) =>
    `Inactive Lab: ${getLabTypeText(notification, context)} for ` +
      `<strong>${getLabContextText(context)}</strong> has had no recent activity.`,
  team_recall_draft_inactive: ({ context }) =>
    `Inactive Recall Draft: <strong>${context.parentName}</strong> ` +
      "has had no recent activity.",
  team_recall_drafts_high_count: ({ notification }) =>
    "Alert: There's currently a large number of recall drafts that should be " +
      `finalised. <strong>${escapeHTML(String(notification.newValue || ""))}</strong>` +
      " drafts are currently open.",
  drug_approve_use: ({ context }) =>
    `<strong>Approval needed:</strong> Drug usage from ${context.contextName}`,
  drug_approve_move: ({ context }) =>
    `<strong>Approval needed:</strong> Relocation: ${context.contextName}`,
  drug_approve_restock: ({ context }) =>
    `<strong>Approval needed:</strong> ${context.contextName}`,
  drug_approve_correction: ({ context }) =>
    `<strong>Approval needed:</strong> Inventory Correction: ${context.contextName}`,
  drug_approve_loss: ({ context }) =>
    `<strong>Approval needed:</strong> Loss Report: ${context.contextName}`,
  drug_approve_void: ({ context }) =>
    `<strong>Approval needed:</strong> ${context.contextName}`,
  drug_approve_audit_start: ({ triggerName }) =>
    `Approval needed: <strong>${triggerName}</strong> requested to START a new audit.`,
  drug_approve_audit_cancel: ({ triggerName }) =>
    `Approval needed: <strong>${triggerName}</strong> requested to CANCEL an active audit.`,
  drug_approve_audit_finalize: ({ triggerName }) =>
    `Approval needed: <strong>${triggerName}</strong> requested to FINALISE an audit.`,
  drug_approval_approved: ({ context }) => {
    const actionText = context.contextName || "request";
    if (actionText.toLowerCase().includes("audit")) {
      return `<strong>Approved:</strong> Your ${actionText} has been approved.`;
    }
    return `<strong>Approved:</strong> Your ${actionText} was approved.`;
  },
  drug_approval_rejected: ({ notification, context }) => {
    const reasonText = stripHtml(notification.newValue) || "None";
    const actionText = context.contextName || "request";
    const baseText = actionText.toLowerCase().includes("audit") ?
      `Your ${actionText} has been rejected.` :
      `Your ${actionText} was rejected.`;
    return `<strong>Rejected:</strong> ${baseText}<br>` +
      `<small style="font-style: italic; font-size: 0.85em;">` +
      `Reason: ${escapeHTML(reasonText)}</small>`;
  },
  drug_transaction_voided: ({ triggerName, context }) =>
    `<strong>Voided:</strong> ${context.contextName} was voided by ${triggerName}.`,
  drug_low_stock_warning: ({ context }) =>
    `<strong>Stock Critical:</strong> ${context.contextName} is critically low.`,
  drug_low_stock_reminder: ({ context }) =>
    `<strong>Low stock:</strong> ${context.contextName} is running low.`,
  event_upcoming: ({ context, notification }) => {
    const thresholdText = getCalendarThresholdLeadText(
        notification,
        {shortMinutes: true},
    );
    return thresholdText ?
      `<strong>${context.contextName}</strong> is coming up in ` +
        `${escapeHTML(thresholdText)}.` :
      `<strong>${context.contextName}</strong> is coming up on ` +
        `${escapeHTML(getCalendarEventDateText(notification))}.`;
  },
  event_today: ({ context }) =>
    `<strong>${context.contextName}</strong> is today.`,
  reminder_upcoming: ({ context, notification }) => {
    const thresholdText = getCalendarThresholdLeadText(
        notification,
        {shortMinutes: true},
    );
    return thresholdText ?
      `Reminder: <strong>${context.contextName}</strong> is coming up in ` +
        `${escapeHTML(thresholdText)}.` :
      `Reminder: <strong>${context.contextName}</strong> is coming up on ` +
        `${escapeHTML(getCalendarReminderDateText(notification))}.`;
  },
  reminder_due: ({ context }) =>
    `Reminder: <strong>${context.contextName}</strong>.`,
  reminder_deleted: ({ context, triggerName }) =>
    `Reminder <strong>${context.contextName}</strong> was deleted by ` +
      `${triggerName}.`,
  leave_upcoming: ({ notification, context }) => {
    const thresholdText = getCalendarThresholdLeadText(
        notification,
        {shortMinutes: true},
    );
    return thresholdText ?
      `<strong>${context.parentName}</strong> has leave starting in ` +
        `${escapeHTML(thresholdText)}.` :
      `<strong>${context.parentName}</strong> has leave starting on ` +
        `${escapeHTML(getCalendarLeaveDateText(notification))}.`;
  },
  leave_today: ({ notification, context }) =>
    `<strong>${context.parentName}</strong> is on leave today` +
      ` (${escapeHTML(getCalendarLeaveDateText(notification))}).`,
  team_leave_today: ({ notification, context }) =>
    `<strong>${context.parentName}</strong> is on leave today` +
      ` (${escapeHTML(getCalendarLeaveDateText(notification))}).`,
  drug_negative_stock: ({ notification, context }) => {
    const value = notification.newValue || "0";
    return `<strong>Negative Stock:</strong> ${context.contextName} has ` +
      `dropped to ${escapeHTML(value)}. Verify immediately.`;
  },
  drug_audit_discrepancy: ({ notification }) =>
    `Audit Discrepancy: Audit finalised with ` +
      `<strong>${escapeHTML(notification.newValue || "0")}</strong> discrepancies.`,
  drug_audit_overdue: ({ notification }) =>
    `Audit Overdue: Stock audit is overdue by ` +
      `<strong>${escapeHTML(notification.newValue || "0")} days</strong>.`,
});

/**
 * Builds in-app notification HTML text for a notification.
 * @param {object} notification Notification document data.
 * @returns {string} Sanitized HTML text.
 */
export function getNotificationText(notification) {
  const definition = getNotificationEventDefinition(notification.triggerEvent);
  const rendererKey = definition?.rendererKey || notification.triggerEvent;
  const renderer = NOTIFICATION_RENDERERS[rendererKey];
  const context = resolveNotificationNames(notification);

  if (!renderer) {
    return sanitizeHTML(`New update on <strong>${context.parentName}</strong>.`);
  }

  return sanitizeHTML(renderer({
    notification,
    triggerName: getTriggerName(notification.triggeredBy),
    context,
    newValue: getReadableNewValue(notification),
  }));
}
